import React from 'react';
import { CalendarDateRangeIcon, CheckCircleIcon, CheckIcon, TruckIcon, UserCircleIcon } from "@heroicons/react/24/solid";
import { CheckBadgeIcon, ClockIcon, CreditCardIcon, HomeIcon, UserIcon } from '@heroicons/react/24/outline';
import { FiFileText, FiMessageCircle } from 'react-icons/fi';
import { FaDollarSign, FaThumbsUp } from 'react-icons/fa';
import { BiCreditCard, BiSearch } from 'react-icons/bi';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RiFindReplaceLine } from 'react-icons/ri';

export default function HostPage() {
    const navigate = useNavigate();
    const { authenticated } = useSelector((state: RootState) => state.auth);
    const { currentUser } = useSelector((state: RootState) => state.user);

    const handleListPropertyClick = () => {
        if (!authenticated) {
            navigate('/login');
            toast('Please login to list a property');
            return;
        }
        if (!currentUser?.is_host) {
            toast('Only hosts can list properties.');
            return;
        }
        navigate('/add-listing-1');
    };

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-r from-orange-500 to-orange-500 py-20 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Become a NomadRoof Host
                    </h1>
                    <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                        Earn money by renting out your property to verified tenants worldwide
                    </p>
                    <button className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition duration-300 shadow-lg"
                        onClick={handleListPropertyClick}
                    >
                        List Your Property
                    </button>
                </div>
            </section>

            {/* Why NomadProof Section */}
            <section className="py-16 px-4">
                <div className='container'>
                    <div className="flex flex-col items-center text-center mb-12">
                        {/* <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <HomeIcon className="h-10 w-10 text-white" />
                        </div> */}
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">WHY NOMADROOF?</h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Streamlining the rental housing application process for rental property seekers.
                            Find tenants directly and pre-qualify them with one simple application.
                        </p>
                    </div>

                    <div className="flex flex-col gap-8">

                        {/* Card 1: Icon left, text right */}
                        <div className="flex items-center gap-6 p-6 rounded-xl shadow-md bg-white dark:bg-gray-800">
                            <div className="flex-shrink-0 bg-orange-500 p-5 rounded-full">
                                <CheckCircleIcon className="h-12 w-12 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-xl mb-2 dark:text-white">Free to publish</h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    List your short-term or long-term rental properties at no cost.
                                    Get direct access to tenants with no commissions.
                                </p>
                            </div>
                        </div>

                        {/* Card 2: Text left, icon right */}
                        <div className="flex items-center justify-between gap-6 p-6 rounded-xl shadow-md bg-white dark:bg-gray-800">
                            <div className="flex-1">
                                <h3 className="font-bold text-xl mb-2 dark:text-white">All in one place</h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Centralized listing platform with powerful search filters.
                                    Tenants find everything they need without visiting multiple sites.
                                </p>
                            </div>
                            <div className="flex-shrink-0 bg-orange-500 p-5 rounded-full">
                                <FiFileText className="h-12 w-12 text-white" />
                            </div>
                        </div>

                        {/* Card 3: Icon left, text right */}
                        <div className="flex items-center gap-6 p-6 rounded-xl shadow-md bg-white dark:bg-gray-800">
                            <div className="flex-shrink-0 bg-orange-500 p-5 rounded-full">
                                <ClockIcon className="h-12 w-12 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-xl mb-2 dark:text-white">Less vacancy periods</h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Increased visibility to a larger audience means your property
                                    stays occupied with minimal downtime between tenants.
                                </p>
                            </div>
                        </div>

                        {/* Card 4: Text left, icon right */}
                        <div className="flex items-center justify-between gap-6 p-6 rounded-xl shadow-md bg-white dark:bg-gray-800">
                            <div className="flex-1">
                                <h3 className="font-bold text-xl mb-2 dark:text-white">Financially committed tenants</h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Secure payments in advance give you peace of mind knowing
                                    tenants are serious about their rental commitment.
                                </p>
                            </div>
                            <div className="flex-shrink-0 bg-orange-500 p-5 rounded-full">
                                <FaDollarSign className="h-12 w-12 text-white" />
                            </div>
                        </div>

                    </div>

                </div>
            </section>

            {/* Publish Your Place Section */}
            <section className="py-16 px-4 bg-gray-100 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-12">
                        {/* <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <FiFileText className="h-10 w-10 text-white" />
                        </div> */}
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">Publish Your Property in 3 Easy Steps</h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Our simple process makes it quick and easy to list your rental property and start earning.
                        </p>
                    </div>
                    <div className="nc-CardAuthorBox relative flex flex-row items-center px-6 py-8  gap-6">
                        {/* Left Side: Icon (50% width) */}
                        <div className="w-1/2 flex gap-10 justify-end">
                            <div className="bg-orange-500 p-6 rounded-full flex items-center justify-center w-24 h-24">
                                <UserIcon className="h-16 w-16 text-white" />
                            </div>
                        </div>

                        {/* Right Side: Text (50% width) */}
                        <div className="w-1/2">
                            <h3 className="font-bold text-xl mb-3 dark:text-white">1. Create Your Account</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Sign up and complete your host profile. We verify all information to ensure
                                a safe community for both hosts and tenants.
                            </p>
                        </div>
                    </div>

                    <div className="nc-CardAuthorBox relative flex flex-row items-center px-6 py-8 gap-6">
                        {/* Left Side: Text (50%) */}
                        <div className="w-1/2 text-right">
                            <h3 className="font-bold text-xl mb-3 dark:text-white">2. List Your Property</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Add property details, photos, amenities, pricing, and house rules.
                                Our intuitive interface makes this process quick and easy.
                            </p>
                        </div>

                        {/* Right Side: Icon (50%) */}
                        <div className="w-1/2 flex">
                            <div className="bg-orange-500 p-6 rounded-full flex items-center justify-center w-24 h-24 shadow-md">
                                <FiFileText className="h-16 w-16 text-white" />
                            </div>
                        </div>
                    </div>

                    <div className="nc-CardAuthorBox relative flex flex-row items-center px-6 py-8 gap-6">
                        {/* Left Side: Icon (50%) */}
                        <div className="w-1/2 flex justify-end">
                            <div className="bg-orange-500 p-6 rounded-full flex items-center justify-center w-24 h-24">
                                <CheckIcon className="h-16 w-16 text-white" />
                            </div>
                        </div>

                        {/* Right Side: Text (50%) */}
                        <div className="w-1/2">
                            <h3 className="font-bold text-xl mb-3 dark:text-white">3. Get Verified & Go Live</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Our team verifies your listing within 24-48 hours. You'll be notified
                                when your property is live and ready for bookings.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tenant Communication Section */}
            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-12">
                        {/* <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <FiMessageCircle className="h-10 w-10 text-white" />
                        </div> */}
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">Connect With Potential Tenants</h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Our communication tools make it easy to find and vet the perfect tenants for your property.
                        </p>
                    </div>

                    <div className="flex flex-row gap-8">
                        {/* Left side - image or content */}
                        <div className="w-1/2 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-xl p-4">
                            <RiFindReplaceLine className='text-orange-500' style={{ width: 200, height: 200 }} />


                            {/* Replace 'asd' with your <img src="..." /> or other content */}
                        </div>

                        {/* Right side - two cards stacked vertically */}
                        <div className="w-1/2 flex flex-col gap-8">
                            {/* Card 1 */}
                            <div className="flex items-center bg-white dark:bg-gray-900 px-6 py-8 rounded-xl shadow-md gap-6">
                                <div className="bg-orange-500 p-6 rounded-full w-24 h-24 flex items-center justify-center shadow-md flex-shrink-0">
                                    <UserCircleIcon className="h-16 w-16 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-2 dark:text-white">Centralized Messaging</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                                        Keep all tenant communications in one place with our in-app messaging system.
                                        Get real-time booking status updates and respond quickly to inquiries.
                                    </p>
                                </div>
                            </div>

                            {/* Card 2 */}
                            <div className="flex items-center bg-white dark:bg-gray-900 px-6 py-8 rounded-xl shadow-md gap-6">
                                <div className="bg-orange-500 p-6 rounded-full w-24 h-24 flex items-center justify-center shadow-md flex-shrink-0">
                                    <CalendarDateRangeIcon className="h-16 w-16 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-2 dark:text-white">Smart Calendar</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                                        Manage your availability with our intuitive calendar system. Block dates,
                                        schedule viewings, and optimize your booking schedule for maximum occupancy.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>
            </section>

            {/* Tenant Matching Section */}
            <section className="py-16 px-4 bg-gray-100  dark:bg-gray-900">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-12">
                        {/* <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <FaThumbsUp className="h-10 w-10 text-white" />
                        </div> */}
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">Find the Perfect Tenant</h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Our platform helps you quickly find reliable, vetted tenants who will respect your property.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="flex flex-col items-center text-center mb-12">
                                <div className="bg-orange-500 p-3 rounded-full mb-4flex-shrink-0">
                                    <BiSearch className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-2 dark:text-white">Direct Tenant Access</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                                        Tenants can book directly or message you through our platform.
                                        All communications and bookings are protected by NomadRoof's policies.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="flex flex-col items-center text-center mb-12">
                                <div className="bg-orange-500 p-3 rounded-full mb-4 flex-shrink-0">
                                    <CheckBadgeIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-2 dark:text-white">Vetted Tenants</h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                                        Every tenant is reviewed before they can book. You'll receive complete
                                        profiles with credentials, payment verification, and previous host reviews.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Payment Security Section */}
            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-12">

                        <h2 className="text-3xl font-bold mb-4 dark:text-white">Secure & Reliable Payments</h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Our payment system protects both hosts and tenants, ensuring you get paid on time, every time.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <TruckIcon className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">Guaranteed Transfers</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Payments are processed securely and transferred to your account
                                after the tenant checks in, according to our payment terms.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <BiCreditCard className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">Fraud Protection</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Our secure payment system prevents scams by releasing funds
                                only at the appropriate stages of the rental process.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <FaThumbsUp className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">Boost Your Bookings</h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Quick responses lead to higher booking rates. Use our mobile
                                app to reply faster and consider "Instant Book" for some properties.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <section className="py-16 px-4 bg-orange-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-6 text-white">Ready to Become a Host?</h2>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition duration-300"
                            onClick={handleListPropertyClick}>
                            List Your Property Now
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}